"use server";

import { z } from "zod";
import { prisma } from "@/lib/db";
import { hashPassword } from "@/lib/password";
import {
  buildPasswordResetUrl,
  createPasswordResetExpiry,
  createPasswordResetToken,
  hashPasswordResetToken,
  isPasswordResetExpired,
} from "@/lib/password-reset";
import { renderEmailHtml, renderEmailText, sendMail } from "@/lib/mail";

const requestPasswordResetSchema = z.object({
  email: z.string().email("Bitte gib eine gültige E-Mail-Adresse ein."),
});

const resetPasswordSchema = z.object({
  token: z.string().min(1, "Ungültiger Reset-Link"),
  newPassword: z.string().min(8, "Neues Passwort muss mindestens 8 Zeichen haben"),
});

const GENERIC_SUCCESS_MESSAGE =
  "Wenn es zu dieser E-Mail ein Konto gibt, haben wir dir einen Reset-Link geschickt.";

export type PasswordResetRequestState = {
  success: boolean;
  message?: string;
  error?: string;
};

export type PasswordResetUpdateState = {
  success: boolean;
  message?: string;
  error?: string;
};

export async function requestPasswordReset(formData: FormData) {
  const parsed = requestPasswordResetSchema.safeParse({
    email: formData.get("email"),
  });

  if (!parsed.success) {
    return { error: parsed.error.flatten().fieldErrors };
  }

  const user = await prisma.user.findUnique({
    where: { email: parsed.data.email },
  });

  if (!user || user.status !== "aktiv") {
    return { success: true, message: GENERIC_SUCCESS_MESSAGE };
  }

  const token = createPasswordResetToken();
  const tokenHash = hashPasswordResetToken(token);
  const expiresAt = createPasswordResetExpiry();
  const resetUrl = buildPasswordResetUrl(token);

  await prisma.passwordResetToken.deleteMany({ where: { userId: user.id } });
  await prisma.passwordResetToken.create({
    data: {
      userId: user.id,
      tokenHash,
      expiresAt,
    },
  });

  const subject = "Passwort für MicMatch zurücksetzen";
  const intro =
    "Du hast eine Zurücksetzung deines Passworts angefragt. Über den Button unten kannst du ein neues Passwort setzen.";

  try {
    await sendMail({
      to: user.email,
      subject,
      text: renderEmailText({
        heading: subject,
        intro,
        ctaLabel: "Passwort zurücksetzen",
        ctaUrl: resetUrl,
        footer: "Wenn du das nicht warst, kannst du diese E-Mail ignorieren.",
      }),
      html: renderEmailHtml({
        heading: subject,
        intro,
        ctaLabel: "Passwort zurücksetzen",
        ctaUrl: resetUrl,
        footer: "Wenn du das nicht warst, kannst du diese E-Mail ignorieren.",
      }),
    });
  } catch (error) {
    console.error("[password-reset] Mailversand fehlgeschlagen", error);
  }

  return { success: true, message: GENERIC_SUCCESS_MESSAGE };
}

export async function resetPassword(formData: FormData) {
  const parsed = resetPasswordSchema.safeParse({
    token: formData.get("token"),
    newPassword: formData.get("newPassword"),
  });

  if (!parsed.success) {
    return { error: parsed.error.flatten().fieldErrors };
  }

  const tokenHash = hashPasswordResetToken(parsed.data.token);
  const resetToken = await prisma.passwordResetToken.findUnique({
    where: { tokenHash },
    include: { user: true },
  });

  if (!resetToken || isPasswordResetExpired(resetToken.expiresAt) || resetToken.user.status !== "aktiv") {
    if (resetToken && isPasswordResetExpired(resetToken.expiresAt)) {
      await prisma.passwordResetToken.delete({ where: { id: resetToken.id } }).catch(() => undefined);
    }

    return {
      error: {
        general: ["Dieser Reset-Link ist ungültig oder abgelaufen."],
      },
    };
  }

  const newHash = await hashPassword(parsed.data.newPassword);

  await prisma.$transaction([
    prisma.user.update({
      where: { id: resetToken.userId },
      data: { passwortHash: newHash },
    }),
    prisma.passwordResetToken.deleteMany({
      where: { userId: resetToken.userId },
    }),
  ]);

  return { success: true };
}

export async function requestPasswordResetAction(
  _prevState: PasswordResetRequestState,
  formData: FormData,
): Promise<PasswordResetRequestState> {
  const result = await requestPasswordReset(formData);

  if (result?.error?.email) {
    return { success: false, error: result.error.email[0] || "Fehler" };
  }

  return {
    success: true,
    message: result.message || GENERIC_SUCCESS_MESSAGE,
  };
}

export async function resetPasswordAction(
  _prevState: PasswordResetUpdateState,
  formData: FormData,
): Promise<PasswordResetUpdateState> {
  const newPassword = formData.get("newPassword")?.toString() || "";
  const confirmPassword = formData.get("confirmPassword")?.toString() || "";

  if (newPassword !== confirmPassword) {
    return {
      success: false,
      error: "Die Passwörter stimmen nicht überein.",
    };
  }

  const result = await resetPassword(formData);

  if (result?.error) {
    const fieldErrors = Object.values(result.error).flat();
    return {
      success: false,
      error: fieldErrors[0] || "Fehler beim Zurücksetzen des Passworts.",
    };
  }

  return {
    success: true,
    message: "Dein Passwort wurde erfolgreich geändert. Du kannst dich jetzt einloggen.",
  };
}
